import { FunctionComponent, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useForgotPasswordMutation } from "../../../app/services/meseeknaApiV2"
import LogoFull from "../../../components/logo-full"
import LoginInput from "./login-input"
import BaseButton from "../../../components/Button/base-button"

type ForgotPasswordFormProps = object

const ForgotPasswordForm: FunctionComponent<ForgotPasswordFormProps> = () => {
  const [forgotPassword] = useForgotPasswordMutation()
  const [emailSent, setEmailSent] = useState<boolean>(false)
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleBack = async () => {
    navigate("/login")
  }

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleFieldChange = (field: string, value: string) => {
    setEmail(value)
    validateEmail(value)
  }

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError("Email is required")
    } else if (!isValidEmail(value)) {
      setEmailError("Invalid email format")
    } else {
      setEmailError("")
    }
  }

  const handleForgotClick = async () => {
    if (email && !emailError) {
      setIsSubmitting(true)
      try {
        // Call the mutation with the new endpoint
        const response = await forgotPassword({ email }).unwrap()

        // The backend will always return success even if the email doesn't exist
        // (this is a security feature)
        if (response.code === 200) {
          setEmailSent(true)
        } else {
          // This should not normally happen since we expect 200 responses
          console.error("Unexpected response:", response)
        }
      } catch (error) {
        console.error("Error sending password reset request:", error)

        // For security reasons, we still show success message even if there was an error
        // This prevents email enumeration attacks
        setEmailSent(true)
      } finally {
        setIsSubmitting(false)
      }
    } else {
      validateEmail(email)
    }
  }

  return (
    <main className="w-[360px] flex flex-col items-center justify-start gap-[32px] text-left text-sm text-gray-900 font-text-md-regular1">
      <LogoFull
        dimensions="/v3/assets/meseekna-logo@2x.png"
        logoWrapWidth="189px"
        logoWrapHeight="31.5px"
        image1Width="188.61px"
        image1Height="30.02px"
      />
      {!emailSent ? (
        <>
          <div className="self-stretch flex flex-col items-center justify-items-center gap-[12px]">
            <h1 className="m-0 relative text-2xl leading-[48px] font-semibold font-inherit">
              Forgot Password
            </h1>
            <div className="self-stretch relative text-base font-text-md-regular1 text-gray-600 text-center">
              Please enter your email address below and we&apos;ll send you a
              link to reset your password.
            </div>
          </div>
          <div className="self-stretch flex flex-col items-center justify-center">
            <LoginInput
              id="email"
              inputType="email"
              inputTitleText="Email"
              inputPlaceholderText="Enter your email"
              required={true}
              defaultValue={email}
              error={emailError}
              setValue={(value) => handleFieldChange("email", value)}
            />
            <div className="self-stretch flex flex-row items-center justify-center gap-4 mt-8">
              <BaseButton
                variant="secondary"
                onClick={handleBack}
                className="w-full">
                Cancel
              </BaseButton>
              <BaseButton
                variant="primary"
                onClick={handleForgotClick}
                className="w-full"
                disabled={isSubmitting}
                type="submit">
                {isSubmitting ? "Submitting..." : "Submit"}
              </BaseButton>
            </div>
          </div>
        </>
      ) : (
        <div className="self-stretch flex flex-col items-center justify-items-center gap-[12px]">
          <h2 className="mb-0 text-gray-700" data-testid="Request Submitted">
            Request Submitted
          </h2>
          <h3 className="mt-0 text-gray-600 text-center">
            If your email is registered, you will receive password reset
            instructions shortly.
          </h3>
          <h3 className="mt-0 text-gray-600 text-center">
            If you have any questions, please contact us by emailing
            customersupport@meseekna.com
          </h3>
          <Link to={"/login"} className="mt-4">
            Click here to return to sign in screen
          </Link>
        </div>
      )}
    </main>
  )
}

export default ForgotPasswordForm
